import type { ComponentType } from 'react';
import { Object3D } from 'three';

import type { CharacterPose, RigBoneTransform } from '../content/schema/character';

export interface CharacterRendererProps {
  pose: CharacterPose | null;
}

export type CharacterRenderer = ComponentType<CharacterRendererProps>;

/**
 * Apply a pose's bone transform onto a Three.js group, falling back to the
 * supplied rest-pose defaults and finally the group's current transform.
 *
 * Exported so game-side and core character renderables share identical pose
 * application semantics - the renderer bodies live with their owning pack, the
 * registry and this helper stay engine-side and game-agnostic.
 */
export function applyPose(
  group: Object3D | null,
  pose: CharacterPose | null,
  boneId: string,
  defaults?: {
    position?: [number, number, number];
    rotation?: [number, number, number];
    scale?: [number, number, number];
  },
): void {
  if (!group) {
    return;
  }

  const transform = pose?.boneTransforms[boneId];
  const position = transform?.position ?? defaults?.position ?? [group.position.x, group.position.y, group.position.z];
  const rotation = transform?.rotation ?? defaults?.rotation ?? [group.rotation.x, group.rotation.y, group.rotation.z];
  const scale = transform?.scale ?? defaults?.scale ?? [group.scale.x, group.scale.y, group.scale.z];

  group.position.set(position[0], position[1], position[2]);
  group.rotation.set(rotation[0], rotation[1], rotation[2]);
  group.scale.set(scale[0], scale[1], scale[2]);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Component-wise lerp of two bone transforms, matching RigBoneTransform's
 * existing Euler-only convention (no quaternions elsewhere in this schema).
 * A field missing from one side passes the other side through unchanged
 * rather than lerping toward zero.
 */
export function blendBoneTransform(
  a: RigBoneTransform | undefined,
  b: RigBoneTransform | undefined,
  t: number,
): RigBoneTransform | undefined {
  if (!a) return b;
  if (!b) return a;
  const blendVec = (
    va: [number, number, number] | undefined,
    vb: [number, number, number] | undefined,
  ): [number, number, number] | undefined => {
    if (!va) return vb;
    if (!vb) return va;
    return [lerp(va[0], vb[0], t), lerp(va[1], vb[1], t), lerp(va[2], vb[2], t)];
  };
  return {
    position: blendVec(a.position, b.position),
    rotation: blendVec(a.rotation, b.rotation),
    scale: blendVec(a.scale, b.scale),
  };
}

/**
 * Blend two poses bone-by-bone. Used for continuous, speed-driven idle/walk
 * blending (a fixed mid-stride lean proportional to speed, not a cycling
 * gait - see the player-avatar rig wiring plan for the trade-off this
 * accepts). `t` is clamped to [0, 1]; either pose may be null (no blend,
 * the other pose passes straight through), and a bone present in only one
 * pose is carried through unchanged rather than blended toward a default.
 */
export function blendPoses(
  a: CharacterPose | null,
  b: CharacterPose | null,
  t: number,
): CharacterPose | null {
  if (!a) return b;
  if (!b) return a;
  const clamped = Math.min(1, Math.max(0, t));
  const boneIds = new Set([...Object.keys(a.boneTransforms), ...Object.keys(b.boneTransforms)]);
  const boneTransforms: Record<string, RigBoneTransform> = {};
  for (const boneId of boneIds) {
    const blended = blendBoneTransform(a.boneTransforms[boneId], b.boneTransforms[boneId], clamped);
    if (blended) {
      boneTransforms[boneId] = blended;
    }
  }
  return { ...a, boneTransforms };
}

// ── Mutable registry ──────────────────────────────────────────────────────────
// Seeded empty; the engine core character pack and games register renderers by
// id. `CharacterDefinition.rendererId` resolves through `getCharacterRenderer`.
const REGISTRY = new Map<string, CharacterRenderer>();

/** Register (or override) character renderers keyed by rendererId. */
export function registerCharacterRenderers(
  renderers: Record<string, CharacterRenderer>,
): void {
  for (const [id, renderer] of Object.entries(renderers)) {
    REGISTRY.set(id, renderer);
  }
}

export function getCharacterRenderer(rendererId: string): CharacterRenderer | null {
  return REGISTRY.get(rendererId) ?? null;
}

export function listCharacterRenderers(): string[] {
  return [...REGISTRY.keys()];
}
